import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import { CoinMark, Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/common/PasswordInput";
import { Label } from "@/components/ui/label";
import { landingPath, useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { FRIEND_KEY } from "@/pages/FriendInvite";
import { DemoButtons } from "@/components/common/DemoButtons";

export const AuthAside = () => (
  <aside className="hero-navy relative hidden flex-col justify-between overflow-hidden p-12 lg:flex">
    <Logo size={80} textClassName="text-4xl text-[#F8FAF8]" className="gap-4" />
    <div>
      <CoinMark size={180} className="coin-float" />
      <h1 className="mt-10 max-w-md text-5xl font-extrabold leading-[1.05]">My cash.<br />My home.<br /><span className="text-mint">My life.</span></h1>
      <p className="mt-6 max-w-sm text-[#F8FAF8]/70">Argent, foyer, biens, documents, garanties : tout votre quotidien, enfin réuni au même endroit.</p>
    </div>
    <div className="flex items-center gap-2 text-xs text-[#F8FAF8]/50"><Building2 className="h-3.5 w-3.5" />Données hébergées et protégées — RGPD by design</div>
  </aside>
);

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      const me = await fn();
      if (me.mfa_required) { setMfa(me.mfa_token); return; }
      const friend = sessionStorage.getItem(FRIEND_KEY);
      navigate(friend ? `/ami/${friend}` : landingPath(me), { replace: true });
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <AuthAside />
      <main className="flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-sm fade-up">
          <div className="lg:hidden"><Logo size={80} textClassName="text-4xl" className="gap-4" /></div>
          <h2 className="mt-10 text-3xl font-extrabold lg:mt-0">Bon retour</h2>
          <p className="mt-2 text-sm text-muted-foreground">Connectez-vous à votre espace CASHMY.</p>
          {mfa ? (
            <form className="mt-8 space-y-4" onSubmit={(e) => { e.preventDefault(); run(() => verifyMfa(mfa, code)); }} data-testid="login-mfa-form">
              <p className="text-sm text-muted-foreground">Saisissez le code à 6 chiffres de votre application d'authentification.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code" maxLength={8} required className="h-12 rounded-xl text-center font-mono text-xl tracking-[0.4em]" data-testid="login-mfa-code-input" />
              {error && <p className="text-sm text-destructive" role="alert" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="login-mfa-submit-button">Valider</Button>
              <button type="button" className="w-full text-sm text-muted-foreground" onClick={() => { setMfa(null); setCode(""); setError(""); }} data-testid="login-mfa-cancel">Retour</button>
            </form>
          ) : (
          <form className="mt-8 space-y-4" onSubmit={(e) => { e.preventDefault(); run(() => login(email, password)); }}>
            <div><Label htmlFor="email">Email</Label><Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-11 rounded-xl" data-testid="login-email-input" /></div>
            <div><Label htmlFor="password">Mot de passe</Label><PasswordInput id="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-11 rounded-xl" data-testid="login-password-input" /></div>
            {error && <p className="text-sm text-destructive" role="alert" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full rounded-full" data-testid="login-submit-button">Se connecter<ArrowRight className="ml-2 h-4 w-4" /></Button>
            <Link to="/forgot-password" className="block text-center text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-password-link">Mot de passe oublié ?</Link>
          </form>
          )}
          <p className="mt-6 text-center text-sm text-muted-foreground">Pas encore de compte ? <Link to="/register" className="font-semibold text-foreground underline-offset-4 hover:underline" data-testid="go-register-link">Créer un compte</Link></p>
          <DemoButtons className="mt-6 rounded-2xl border border-border p-4" />
          <p className="mt-2 text-center text-sm text-muted-foreground">Vous êtes un professionnel ? <Link to="/partenaires/inscription" className="font-semibold text-foreground underline-offset-4 hover:underline" data-testid="go-partner-register-link">Devenir partenaire</Link></p>
        </div>
      </main>
    </div>
  );
}
